import React from "react";
import { useWebSocket } from "../hooks/useWebSocket";
import { useApi } from "../hooks/useApi";
import { format } from "date-fns";

const card = {
  background: "#0d1224", border: "1px solid #1e2a45",
  borderRadius: "10px", padding: "14px",
  display: "flex", flexDirection: "column", maxHeight: "380px",
};

const RECOVERY = ["CLOSE","CLOSE_ALL","PARTIAL_CLOSE","ADJUST_SL","REVERSE"];

function meta(msg) {
  const t = msg.type, a = msg.action || "";
  if (t === "trade_opened")
    return { icon: a === "BUY" ? "🟢" : "🔴", color: a === "BUY" ? "#4ade80" : "#f87171",
             label: `OPENED ${msg.symbol} ${a} ${msg.lots}L @ ${msg.price} [${msg.tranche||""}]`, tag: "ENTRY" };
  if (t === "trade_closed")
    return { icon: "✅", color: "#a78bfa",
             label: `CLOSED ${msg.symbol} $${msg.profit?.toFixed(2)} — ${msg.reason}`, tag: msg.profit >= 0 ? "WIN" : "LOSS" };
  if (t === "sl_modified")
    return { icon: "🔒", color: "#60a5fa",
             label: `${msg.symbol} SL → ${msg.new_sl?.toFixed(5)} (${msg.reason})`, tag: "SL" };
  if (t === "brain_event") {
    if (RECOVERY.includes(a))
      return { icon: "🔄", color: "#fb923c", label: `RECOVERY ${msg.symbol} → ${a}`, tag: "RECOVERY" };
    if (a === "HOLD")
      return { icon: "⏸", color: "#475569", label: `${msg.symbol} HOLD ${msg.gate ? "⛔"+msg.gate.split(":")[0] : ""}`, tag: "HOLD" };
    return { icon: "🧠", color: a === "BUY" ? "#4ade80" : "#f87171",
             label: `${msg.symbol} ${a} ${msg.confidence ? (msg.confidence*100).toFixed(0)+"%" : ""}`, tag: "SIGNAL" };
  }
  if (t === "pending")
    return { icon: "📡", color: "#f59e0b",
             label: `PENDING ${msg.symbol} ${msg.action} ${msg.lots}L — ${msg.status} ${msg.age_sec}s`, tag: "PENDING" };
  return { icon: "•", color: "#475569", label: JSON.stringify(msg).slice(0, 60), tag: "" };
}

const tagColors = {
  ENTRY: "#166534", WIN: "#14532d", LOSS: "#7f1d1d", SL: "#1e3a5f",
  RECOVERY: "#7c2d12", HOLD: "#1e2a45", SIGNAL: "#1e3a5f", PENDING: "#78350f",
};

export default function ActivityFeed() {
  const { messages, connected } = useWebSocket("feed");
  const { data: intel }         = useApi("/intelligence", 15000);

  // Build combined feed: pending signals from REST + WS events
  const pending = (intel?.pending_signals || []).map(s => ({ ...s, type: "pending" }));
  const wsEvents = messages.filter(m =>
    ["trade_opened","trade_closed","sl_modified","brain_event"].includes(m.type)
  ).slice(0, 30);

  const allEvents = [...pending, ...wsEvents];

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", flexShrink: 0 }}>
        <span style={{ fontWeight: 600, color: "#94a3b8", fontSize: "13px" }}>⚡ Live Activity</span>
        <span style={{ fontSize: "10px", color: connected ? "#22c55e" : "#ef4444" }}>
          {connected ? "● LIVE" : "● OFFLINE"}
        </span>
      </div>

      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "3px" }}>
        {allEvents.length === 0 && (
          <div style={{ color: "#334155", fontSize: "11px", textAlign: "center", paddingTop: "20px" }}>
            Engine running — waiting for market signals...
          </div>
        )}
        {allEvents.map((msg, i) => {
          const { icon, color, label, tag } = meta(msg);
          const isRecovery = tag === "RECOVERY";
          return (
            <div key={i} style={{
              display: "flex", gap: "6px", alignItems: "flex-start",
              padding: "4px 6px", borderRadius: "5px",
              background: isRecovery ? "#1a0a00" : i === 0 ? "#0f172a" : "transparent",
              borderLeft: `2px solid ${color}`,
            }}>
              <span style={{ fontSize: "11px", flexShrink: 0 }}>{icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: "10px", color, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {label}
                </div>
                <div style={{ fontSize: "9px", color: "#334155" }}>
                  {msg.ts ? format(new Date(msg.ts), "HH:mm:ss") : ""}
                </div>
              </div>
              {tag && (
                <span style={{
                  fontSize: "9px", padding: "1px 5px", borderRadius: "3px",
                  background: tagColors[tag] || "#1e2a45", color: "#e2e8f0",
                  flexShrink: 0, fontWeight: 600,
                }}>{tag}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
